import "./nav"
import { application } from "./application"

// ── Scroll restoration ─────────────────────────────────────────────────
// The browser's automatic scroll restoration fires before lazy images, fonts,
// and CSS have all settled. On long pages this causes the saved scroll
// position to land too low (often at the document bottom) when content shifts
// after the initial paint. We take manual control: save scrollY on unload,
// then restore it inside requestAnimationFrame after the `load` event — by
// which point all visible-area assets are stable.
if ("scrollRestoration" in history) {
  history.scrollRestoration = "manual"
}

const scrollKey = () => `scroll:${location.pathname}${location.search}`

window.addEventListener("beforeunload", () => {
  sessionStorage.setItem(scrollKey(), String(window.scrollY))
})

window.addEventListener("load", () => {
  const saved = sessionStorage.getItem(scrollKey())
  if (saved === null) return
  sessionStorage.removeItem(scrollKey())
  requestAnimationFrame(() => window.scrollTo(0, parseInt(saved, 10)))
})

const loaded = new Set()

async function loadControllersOnce(key, loader) {
  if (loaded.has(key)) return
  loaded.add(key)
  await loader()
}

function hasController(name) {
  return document.querySelector(`[data-controller~="${name}"]`)
}

async function hydratePageControllers() {
  if (hasController("compare-search") || hasController("compare-dots")) {
    await loadControllersOnce("compare", async () => {
      const [{ default: CompareSearchController }, { default: CompareDotsController }] = await Promise.all([
        import("./compare_search_controller"),
        import("./compare_dots_controller")
      ])
      application.register("compare-search", CompareSearchController)
      application.register("compare-dots", CompareDotsController)
    })
  }

  if (hasController("compare-bar")) {
    await loadControllersOnce("compare-bar", async () => {
      const { default: CompareBarController } = await import("./compare_bar_controller")
      application.register("compare-bar", CompareBarController)
    })
  }

  if (
    hasController("rewards-calculator") ||
    hasController("mortgage-calculator") ||
    hasController("payoff-calculator") ||
    hasController("balance-transfer-calculator") ||
    hasController("monthly-payment-calculator") ||
    hasController("interest-calculator")
  ) {
    await loadControllersOnce("calculators", async () => {
      const [
        { default: RewardsCalculatorController },
        { default: MortgageCalculatorController },
        { default: PayoffCalculatorController },
        { default: BalanceTransferCalculatorController },
        { default: MonthlyPaymentCalculatorController },
        { default: InterestCalculatorController }
      ] = await Promise.all([
        import("./rewards_calculator_controller"),
        import("./mortgage_calculator_controller"),
        import("./payoff_calculator_controller"),
        import("./balance_transfer_calculator_controller"),
        import("./monthly_payment_calculator_controller"),
        import("./interest_calculator_controller")
      ])

      application.register("rewards-calculator", RewardsCalculatorController)
      application.register("mortgage-calculator", MortgageCalculatorController)
      application.register("payoff-calculator", PayoffCalculatorController)
      application.register("balance-transfer-calculator", BalanceTransferCalculatorController)
      application.register("monthly-payment-calculator", MonthlyPaymentCalculatorController)
      application.register("interest-calculator", InterestCalculatorController)
    })
  }

  if (hasController("tabs") || hasController("live-search")) {
    await loadControllersOnce("wallet", async () => {
      const [{ default: TabsController }, { default: LiveSearchController }] = await Promise.all([
        import("./tabs_controller"),
        import("./live_search_controller")
      ])
      application.register("tabs", TabsController)
      application.register("live-search", LiveSearchController)
    })
  }

  if (hasController("passkey-authentication")) {
    await loadControllersOnce("passkey-authentication", async () => {
      const { default: PasskeyAuthenticationController } = await import("./passkey_authentication_controller")
      application.register("passkey-authentication", PasskeyAuthenticationController)
    })
  }

  if (hasController("passkey-registration")) {
    await loadControllersOnce("passkey-registration", async () => {
      const { default: PasskeyRegistrationController } = await import("./passkey_registration_controller")
      application.register("passkey-registration", PasskeyRegistrationController)
    })
  }

  if (hasController("registration-form")) {
    await loadControllersOnce("registration-form", async () => {
      const { default: RegistrationFormController } = await import("./registration_form_controller")
      application.register("registration-form", RegistrationFormController)
    })
  }

  if (hasController("contact-recaptcha")) {
    await loadControllersOnce("contact-recaptcha", async () => {
      const { default: ContactRecaptchaController } = await import("./contact_recaptcha_controller")
      application.register("contact-recaptcha", ContactRecaptchaController)
    })
  }

  if (hasController("embed-snippet")) {
    await loadControllersOnce("embed-snippet", async () => {
      const { default: EmbedSnippetController } = await import("./embed_snippet_controller")
      application.register("embed-snippet", EmbedSnippetController)
    })
  }
}

document.addEventListener("turbo:load", hydratePageControllers)

// Store the current path in a cookie just before navigating to an apply link
// so the leads controller can attribute the click to its origin page without
// polluting the URL with params. Additive — the <a href> handles navigation
// natively; this listener doesn't preventDefault.
document.addEventListener("click", (e) => {
  const applyLink = e.target.closest("[data-apply-link]")
  if (!applyLink) return
  document.cookie = `apply_from=${encodeURIComponent(location.pathname)};path=/;SameSite=Strict`
})

// Calculator tab strip is overflow-x-auto. After page load (or a turbo-frame
// swap when switching tabs) the strip is at scrollLeft=0, leaving the active
// tab off-screen to the right (e.g. Interest is the 5th tab). Center the
// active tab horizontally — modify only nav.scrollLeft so the page itself
// never moves vertically.
function scrollActiveCalculatorTabIntoView() {
  const nav = document.querySelector("#calculator-tabs nav")
  const active = nav?.querySelector('[aria-current="page"]')
  if (!nav || !active) return
  const navRect = nav.getBoundingClientRect()
  const activeRect = active.getBoundingClientRect()
  nav.scrollLeft += (activeRect.left - navRect.left) - (navRect.width - activeRect.width) / 2
}
document.addEventListener("turbo:load", scrollActiveCalculatorTabIntoView)
document.addEventListener("turbo:frame-load", scrollActiveCalculatorTabIntoView)
